import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import TenYearComparison from "@/components/site/TenYearComparison";

const PREV_BILL = 295;
const START_PAYMENT = 244.47;

const monthlyDiff = PREV_BILL - START_PAYMENT;
const yearlyDiff = monthlyDiff * 12;
const pctBelow = (monthlyDiff / PREV_BILL) * 100;
const usd = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const HIGHLIGHTS = [
  { id: "monthly", text: `${usd(monthlyDiff)}/month lower starting payment` },
  { id: "first-year", text: `${usd(yearlyDiff)} first-year payment difference, before remaining utility charges` },
  { id: "percent", text: `Approximately ${pctBelow.toFixed(1)}% below the previous average electric bill, before remaining utility charges` },
];

const SYSTEM = [
  { id: "panels", label: "Solar panels", value: "34" },
  { id: "size", label: "System size", value: "14.96 kW" },
  { id: "production", label: "Estimated annual production", value: "20,955 kWh", estimate: true },
  { id: "offset", label: "Estimated annual energy offset", value: "100%", estimate: true },
  { id: "escalator", label: "Annual agreement escalator", value: "2.99%" },
];

function CompareCard({ testid, label, value, accent }) {
  return (
    <div data-testid={testid} className={`rounded-3xl p-7 md:p-9 ${accent ? "bg-[#0F382C] text-[#FEF3C7]" : "bg-white border border-[#E7DFCF] text-[#0F382C]"}`}>
      <div className={`text-sm font-semibold uppercase tracking-wider ${accent ? "text-[#FCD34D]" : "text-slate-500"}`}>{label}</div>
      <div className="mt-3 font-serif-display text-4xl md:text-5xl font-semibold">
        {value}<span className="text-lg md:text-xl font-normal opacity-80">/month</span>
      </div>
    </div>
  );
}

function SystemAtAGlance() {
  return (
    <div className="card-warm p-6 md:p-8" data-testid="customer-example-system">
      <h3 className="font-serif-display text-xl text-[#0F382C]">System at a glance</h3>
      <dl className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
        {SYSTEM.map((s) => (
          <div key={s.id} data-testid={`system-${s.id}`}>
            <dt className="text-xs text-slate-500">
              {s.label}
              {s.estimate && <span className="ml-1 inline-block rounded-full bg-[#FEF3C7] text-[#B45309] px-2 py-0.5 text-[10px] font-semibold uppercase">Proposal estimate</span>}
            </dt>
            <dd className="mt-1 font-serif-display text-2xl text-[#0F382C]">{s.value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-5 text-sm text-slate-600 leading-relaxed" data-testid="system-offset-note">
        Production and offset are estimates from the proposal. A 100% estimated annual energy offset means the system is
        designed to produce roughly as much electricity over a year as the home was estimated to use. It does not mean a $0
        utility bill, and it does not mean solar supplies every hour's electricity.
      </p>
    </div>
  );
}

export default function CustomerExample() {
  return (
    <section className="py-20 md:py-28 bg-[#F5EFE2]" data-testid="customer-example-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="eyebrow">Customer Example</div>
          <h2 className="mt-3 font-serif-display text-3xl md:text-5xl font-semibold text-[#0F382C] leading-tight" data-testid="customer-example-title">
            An Actual Customer's Solar Comparison
          </h2>
          <p className="mt-4 text-slate-600 text-base md:text-lg">
            One of Jeremy's previous customers averaged $295 per month in electricity costs. His solar agreement started at
            $244.47 per month, with a 2.99% annual escalator. Here's how the starting costs compared.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <CompareCard testid="compare-previous-bill" label="Previous average electric bill" value={`$${PREV_BILL}`} />
          <CompareCard testid="compare-solar-payment" label="Starting solar agreement payment" value={usd(START_PAYMENT)} accent />
        </div>

        <ul className="mt-5 grid md:grid-cols-3 gap-4">
          {HIGHLIGHTS.map((h) => (
            <li key={h.id} data-testid={`highlight-${h.id}`} className="rounded-2xl border-l-4 border-[#D97706] bg-white p-5 font-semibold text-[#0F382C] leading-snug">
              {h.text}
            </li>
          ))}
        </ul>

        <div className="mt-5 flex gap-3 rounded-2xl bg-white/70 border border-[#E7DFCF] p-5 text-sm text-slate-700 leading-relaxed" data-testid="customer-example-disclosure">
          <Info className="w-5 h-5 shrink-0 text-[#B45309] mt-0.5" />
          <p>
            This example compares a customer's previous average electric bill with the starting solar agreement payment.
            Remaining utility charges are not included because post-installation utility bills have not been provided. The
            solar payment increases 2.99% annually. Actual costs and savings depend on usage, system performance, utility
            rates, and agreement terms. Production and energy-offset figures are estimates from the proposal.
          </p>
        </div>

        <div className="mt-10">
          <SystemAtAGlance />
        </div>

        <TenYearComparison />

        <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-5">
          <h3 className="font-serif-display text-2xl md:text-3xl text-[#0F382C]">Wondering how your home compares?</h3>
          <Link to="/qualify" className="btn-solar text-base sm:ml-auto self-start" data-testid="customer-example-cta">
            Have Jeremy Review My Bill <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
